import type {
  AutomationApiActionsData,
  AutomationFormData,
} from './AutomationModal'
import {
  isTimeBasedTrigger,
  isDaysSinceTrigger,
  isStageDuration,
  STAGE_DURATION_DEFAULT_OPERATOR,
  DAYS_SINCE_DEFAULT_UNIT,
} from '../../constants/automation-triggers'

/**
 * Extracts time-based trigger fields from form values.
 * Returns an empty object if the trigger is not time-based.
 */
function getTimeBasedFields(values: AutomationFormData) {
  if (!isTimeBasedTrigger(values.trigger_type)) {
    return {}
  }

  return {
    comparison_operator: isStageDuration(values.trigger_type)
      ? STAGE_DURATION_DEFAULT_OPERATOR
      : values.comparison_operator || null,
    duration_value: values.duration_value ?? null,
    duration_unit: isDaysSinceTrigger(values.trigger_type)
      ? DAYS_SINCE_DEFAULT_UNIT
      : values.duration_unit || null,
  }
}

/**
 * Cleans automation action payload by returning only valid action fields based on action type
 */
export function cleanAutomationActionPayload(
  values: AutomationFormData,
  orderIndex: number = 0
): AutomationApiActionsData {
  return {
    action_type: values.action_type,
    target_user_id:
      values.action_type === 'notify_user' ||
      values.action_type === 'change_account_owner'
        ? values.target_user_id
        : null,
    target_stage_id:
      values.action_type === 'change_sales_stage'
        ? values.target_stage_id
        : null,
    target_tag_id:
      values.action_type === 'add_tag' ? values.target_tag_id : null,
    target_campaign_id:
      values.action_type === 'add_to_campaign' ||
      values.action_type === 'remove_from_campaign'
        ? values.target_campaign_id
        : null,
    content: values.action_type === 'add_company_note' ? values.content : null,
    order_index: orderIndex,
    attachments: values.attachments || [],
  }
}

/**
 * Cleans automation condition payload by setting unused target IDs to null based on trigger type
 */
export function cleanAutomationConditionPayload(
  values: AutomationFormData,
  orderIndex: number = 0
) {
  const cleanedTargets = {
    target_user_id:
      values.trigger_type === 'field_changed_account_owner'
        ? values.trigger_target_user_id
        : null,
    target_stage_id:
      values.trigger_type === 'field_changed_sales_stage' ||
      isStageDuration(values.trigger_type)
        ? values.trigger_target_stage_id
        : null,
  }

  return {
    trigger_type: values.trigger_type,
    ...cleanedTargets,
    ...getTimeBasedFields(values),
    order_index: orderIndex,
    attachments: values.attachments || [],
  }
}

/**
 * Cleans automation form data payload by returning only valid form fields based on action type
 */
export function cleanAutomationFormDataPayload(
  values: AutomationFormData
): AutomationFormData {
  const timeBasedFields = getTimeBasedFields(values)

  return {
    action_type: values.action_type,
    name: values.name,
    description: values.description,
    trigger_type: values.trigger_type,
    trigger_target_stage_id:
      values.trigger_type === 'field_changed_sales_stage' ||
      isStageDuration(values.trigger_type)
        ? values.trigger_target_stage_id
        : null,
    trigger_target_user_id:
      values.trigger_type === 'field_changed_account_owner'
        ? values.trigger_target_user_id
        : null,
    comparison_operator: timeBasedFields.comparison_operator ?? null,
    duration_value: timeBasedFields.duration_value ?? null,
    duration_unit: timeBasedFields.duration_unit ?? null,
    target_user_id:
      values.action_type === 'notify_user' ||
      values.action_type === 'change_account_owner'
        ? values.target_user_id
        : null,
    target_stage_id:
      values.action_type === 'change_sales_stage'
        ? values.target_stage_id
        : null,
    target_tag_id:
      values.action_type === 'add_tag' ? values.target_tag_id : null,
    target_campaign_id:
      values.action_type === 'add_to_campaign' ||
      values.action_type === 'remove_from_campaign'
        ? values.target_campaign_id
        : null,
    content: values.action_type === 'add_company_note' ? values.content : null,
    attachments: values.attachments || [],
  }
}
